import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FolderOpen, Search, ShieldCheck, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import AppShell from "@/components/platform/AppShell";
import { EmptyState, PageTitle } from "@/components/platform/Badges";
import { FileObjectCard } from "@/components/platform/FileObjectCard";
import { FileUploadDialog } from "@/components/platform/FileUploadDialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const SOURCES = [["", "All files"], ["evidence_vault", "Evidence Vault"], ["file_service", "Uploaded here"]];

export default function FileService() {
  const auth = useAuth();
  const [files, setFiles] = useState([]);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");
  const [upload, setUpload] = useState(false);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/platform/files", { params: { q: q || undefined, source: source || undefined } });
    setFiles(data.files);
  }, [q, source]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const verifyAll = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/platform/files/verify-all");
      toast.success(`Integrity sweep: ${data.verified} verified, ${data.failed} failed`);
      load();
    } finally {
      setChecking(false);
    }
  };

  const counts = {
    total: files.length,
    verified: files.filter((f) => f.integrity?.status === "verified").length,
    failed: files.filter((f) => f.integrity?.status === "failed").length,
  };

  return (
    <AppShell testId="file-service-page">
      <PageTitle eyebrow="File service" title="Files & Integrity"
                 subtitle="Versioned storage shared with the Evidence Vault. Every version carries a SHA-256 fingerprint you can re-verify at any time."
                 actions={<>
                   {auth.can("files", "edit") && (
                     <Button variant="outline" onClick={verifyAll} disabled={checking} data-testid="files-verify-all">
                       <ShieldCheck className="w-4 h-4 mr-1" /> {checking ? "Verifying…" : "Verify all"}
                     </Button>
                   )}
                   {auth.can("files", "create") && (
                     <Button onClick={() => setUpload(true)} className="bg-[#008080] hover:bg-[#008080]/90" data-testid="files-upload-button">
                       <Upload className="w-4 h-4 mr-1" /> Upload
                     </Button>
                   )}
                 </>} />
      <div className="grid grid-cols-3 gap-3 max-w-xl">
        {[["Files", counts.total, "text-[#003366]"], ["Verified", counts.verified, "text-emerald-700"], ["Failed", counts.failed, "text-red-600"]].map(([l, v, c]) => (
          <div key={l} className="bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid={`files-kpi-${l.toLowerCase()}`}>
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{l}</div>
            <div className={`text-2xl font-bold font-mono ${c}`}>{v}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name" className="h-9 pl-8 w-[220px] text-xs bg-white"
                 data-testid="files-search" />
        </div>
        {SOURCES.map(([k, l]) => (
          <button key={k || "all"} onClick={() => setSource(k)} data-testid={`files-source-${k || "all"}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    source === k ? "bg-[#003366] text-white border-[#003366]" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {l}
          </button>
        ))}
      </div>
      {files.length === 0 ? (
        <EmptyState icon={FolderOpen} title="No files yet" hint="Upload a file, or add evidence in the Evidence Vault." testId="files-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="files-grid">
          {files.map((f) => <FileObjectCard key={f.id} file={f} onChanged={load} />)}
        </div>
      )}
      <FileUploadDialog open={upload} onOpenChange={setUpload} onUploaded={load} />
    </AppShell>
  );
}
